import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, statusTone } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { ReceiptText } from "lucide-react";

const ETYPES = ["Program Expense", "Salaries", "Occupancy", "Office & Supplies", "Fundraising", "Consulting", "Management & General"];
const EACC = { "Program Expense": "5010", "Salaries": "5020", "Occupancy": "5030", "Office & Supplies": "5040", "Fundraising": "5050", "Consulting": "5060", "Management & General": "5060" };

export default function Expenses() {
  const { user } = useAuth();
  const canApprove = ["admin", "finance_officer"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ expense_date: new Date().toISOString().slice(0, 10), expense_type: "Program Expense", vendor_id: "", fund_id: "", amount: "", description: "", payment_method: "Check" });

  const load = useCallback(() => {
    api.get("/expenses").then((r) => setRows(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/funds").then((r) => setFunds(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount), account_number: EACC[form.expense_type], vendor_id: form.vendor_id || null, fund_id: form.fund_id || null });
      toast.success("Expense submitted for approval");
      setOpen(false);
      setForm({ ...form, amount: "", description: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try { await api.patch(`/expenses/${id}/${action}`); toast.success(action === "approve" ? "Approved" : "Marked paid & posted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!rows) return <Loader />;
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const unpaid = rows.filter((r) => r.payment_status === "Unpaid").reduce((s, r) => s + r.amount, 0);
  const pending = rows.filter((r) => r.approval_status === "Pending").length;

  const columns = [
    { key: "expense_date", label: "Date", render: (r) => fmtDate(r.expense_date) },
    { key: "vendor_name", label: "Vendor" },
    { key: "expense_type", label: "Type", className: "text-[#5C5C54]" },
    { key: "amount", label: "Amount", align: "right", render: (r) => <span className="text-outflow font-semibold">{fmtUSD(r.amount)}</span> },
    { key: "approval_status", label: "Approval", render: (r) => <StatusBadge tone={statusTone(r.approval_status)}>{r.approval_status}</StatusBadge> },
    { key: "payment_status", label: "Payment", render: (r) => <StatusBadge tone={statusTone(r.payment_status)}>{r.payment_status}</StatusBadge> },
    { key: "actions", label: "", align: "right", render: (r) => {
      if (!canApprove) return null;
      if (r.approval_status === "Pending") return <button onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`} className="text-xs text-forest font-medium hover:underline">Approve</button>;
      if (r.payment_status === "Unpaid") return <button onClick={() => act(r.id, "pay")} data-testid={`pay-${r.id}`} className="text-xs text-forest font-medium hover:underline">Mark Paid</button>;
      return null;
    }},
  ];

  return (
    <div>
      <PageHeader title="Expense Tracking" subtitle="Submit, approve and pay expenses with segregation-of-duties controls."
        action={<AddButton onClick={() => setOpen(true)} label="New Expense" testid="add-expense-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Total Expenses" value={fmtUSD(total)} icon={ReceiptText} accent="#C2410C" />
        <KpiCard label="Unpaid" value={fmtUSD(unpaid)} accent="#B45309" />
        <KpiCard label="Awaiting Approval" value={pending} />
      </div>
      <DataTable columns={columns} rows={rows} testid="expenses-table" />

      <Modal open={open} onOpenChange={setOpen} title="New Expense" onSubmit={submit} busy={busy} testid="expense-modal">
        <Field label="Date"><TextInput type="date" required value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></Field>
        <Field label="Type"><SelectInput value={form.expense_type} onChange={(e) => setForm({ ...form, expense_type: e.target.value })} options={ETYPES} /></Field>
        <Field label="Vendor"><SelectInput value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
          options={[{ value: "", label: "— None —" }, ...vendors.map((v) => ({ value: v.id, label: v.vendor_name }))]} /></Field>
        <Field label="Fund"><SelectInput value={form.fund_id} onChange={(e) => setForm({ ...form, fund_id: e.target.value })}
          options={[{ value: "", label: "— None —" }, ...funds.map((f) => ({ value: f.id, label: f.fund_name }))]} /></Field>
        <Field label="Amount (USD)"><TextInput type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
        <Field label="Payment Method"><SelectInput value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} options={["Check", "ACH", "Credit Card", "Wire"]} /></Field>
        <Field label="Description" span={2}><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}
